import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  FormHelperText,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Switch,
  FormControlLabel
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { url } from "../api/axios_client";
import axios from "../api/axios_client";
import { useTranslation } from "react-i18next";

const AddIndexDialog = ({ open, onClose, onAdd, newIndexType }) => {
  const [types, setTypes] = useState([]);
  const [specialLegends, setSpecialLegends] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  
  // Add these new state variables
  const [touchedFields, setTouchedFields] = useState(new Set());
  const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
  
  const getErrorMessage = (error, msg = '') => {
    if (error.response?.data) {
      const isArabic = i18n.language === 'ar';
      return isArabic ? 
        (error.response.data.message_ar || error.response.data.message) : 
        error.response.data.message;
    }
    if (msg) return msg;
    return "An error occurred";
  };

  const [indexData, setIndexData] = useState({
    name: null,
    name_ar: null,
    type_id: newIndexType,
    unit: null,
    unit_ar: null,
    scale: 1.0,
    offset: 0,
    flags: [],
    flagsInput: "",
    source: null,
    source_ar: null,
    legends: [],
    legendType: "ranges",
    minValue: null,
    temporal_resolution: null,
    temporal_resolution_ar: null,
    maxValue: null,
    provider: null,
    provider_ar: null,
    description: null,
    description_ar: null,
    resolution: null,
    terms: null,
    terms_ar: null,
    citation: null,
    citation_ar: null,
    nodata: null,
    methodology: null,
    methodology_ar: null,
    country: null,
    custom: false,
  });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${url}/index-types`);
        const data = await response.data;

        // Ensure data is an array before setting state
        if (Array.isArray(data)) {
          setTypes(data);
        } else {
          console.error("Expected array of types but received:", data);
          setTypes([]); // Set empty array as fallback
        }
      } catch (error) {
        console.error("Error fetching index types:", error);
        setTypes([]); // Set empty array on error
      }
    };
    fetchTypes();
  }, []);

  const [legendColors, setLegendColors] = useState([
    "#FF0000", // Default color for <=0
    "#FFA500", // Default color for first range
    "#00FF00", // Default color for second range
    "#0000FF", // Default color for >max
  ]);

  const [errors, setErrors] = useState({});
  const [legendErrors, setLegendErrors] = useState([]);

  const [submitLoading, setSubmitLoading] = useState(false);

  // Updated useEffect to reset new states
  useEffect(() => {
    if (open) {
      setErrors({});
      setSubmitLoading(false);
      setTouchedFields(new Set());
      setHasSubmitAttempted(false);
    }
  }, [open]);

  useEffect(() => {
    if (!indexData.legends || indexData.legends.length === 0) return;

    const errors = validateLegendsContainSpecialValues();
    setLegendErrors(errors);

    setErrors((prev) => ({
      ...prev,
      legends:
        errors.length > 0
          ? {
              message: errors.map((err) => `• ${err}`).join("\n"),
            }
          : null,
    }));
  }, [
    indexData.legends,
    indexData.flags,
    indexData.nodata,
    indexData.legendType,
  ]);

  const validateField = (name, value) => {
    const constraints = {
      name: { required: true, maxLength: 40, message: "Name is required" },
      name_ar: { required: true, maxLength: 40, message: "الاسم مطلوب" },
      type_id: { required: true, message: "Type is required" },
      scale: { required: true, type: "number", message: "Scale is required" },
      offset: { required: true, type: "number", message: "Offset is required" },
      nodata: { required: true, message: "No Data Value is required" },
      resolution: {
        required: true,
        maxLength: 40,
        message: "Resolution is required",
      },
      provider: {
        required: true,
        maxLength: 120,
        message: "Provider is required",
      },
      provider_ar: { required: true, maxLength: 120, message: "المزود مطلوب" },
      description: {
        required: true,
        maxLength: 512,
        message: "Description is required",
      },
      description_ar: { required: true, maxLength: 512, message: "الوصف مطلوب" },
      source: { required: true, maxLength: 120, message: "Source is required" },
      source_ar: { required: true, maxLength: 120, message: "المصدر مطلوب" },
      citation: {
        required: true,
        maxLength: 512,
        message: "Citation is required",
      },
      citation_ar: {
        required: true,
        maxLength: 512,
        message: "الاقتباس مطلوب",
      },
      methodology: {
        required: true,
        maxLength: 512,
        message: "Methodology is required",
      },
      methodology_ar: {
        required: true,
        maxLength: 512,
        message: "المنهجية مطلوبة",
      },
      terms: { 
        required: true, 
        maxLength: 512, 
        message: "Terms is required" 
      },
      terms_ar: { 
        required: true, 
        maxLength: 512, 
        message: "الشروط مطلوبة" 
      },
      temporal_resolution: {
        required: true,
        message: "Temporal resolution is required"
      },
      temporal_resolution_ar: {
        required: true,
        message: "الدقة الزمنية مطلوبة"
      },
      // unit: { required: true, message: "Unit is required" },
      // unit_ar: { required: true, message: "الوحدة مطلوبة" },
      minValue: { required: true, message: "Lower possible value is required" },
      maxValue: { required: true, message: "Upper possible value is required" },
    };
    

    const error = {};

    if (constraints[name] === "minValue" || constraints[name] === "maxValue") {
      if (indexData.legendType === "ranges" || indexData.legendType === "gradient") {
        if (value === null || value === undefined || value === "") {
          error.message = "Must be a valid number";
        }
      }
    }

    if (constraints[name]) {
      // Check required
      if (
        constraints[name].required &&
        (value === null || value === undefined || value === "")
      ) {
        error.message = constraints[name].message;
      }
      // Check max length for string values
      else if (
        constraints[name].maxLength &&
        typeof value === "string" &&
        value.length > constraints[name].maxLength
      ) {
        error.message = `Maximum ${constraints[name].maxLength} characters allowed`;
      }
      // Check number type
      else if (constraints[name].type === "number" && isNaN(Number(value))) {
        error.message = "Must be a valid number";
      }
    }

    return error;
  };

  // Helper function to check if field is required and empty
  const isFieldRequired = (fieldName) => {
    const requiredFields = [
      "name", "name_ar", "type_id", "scale", "offset", "nodata", "resolution",
      "provider", "provider_ar", "description", "description_ar", "source", 
      "source_ar", "citation", "citation_ar", "methodology", "methodology_ar",
      "terms", "terms_ar", "temporal_resolution", "temporal_resolution_ar"
    ];
    
    // Add minValue and maxValue for ranges and gradient types
    if (indexData.legendType === "ranges" || indexData.legendType === "gradient") {
      requiredFields.push("minValue", "maxValue");
    }
    
    return requiredFields.includes(fieldName);
  };

  const isFieldEmpty = (value) => {
    return value === null || value === undefined || value === "";
  };

  // Updated getFieldError function
  const getFieldError = (fieldName, value) => {
    // Only show validation errors if the field has been touched or submit was attempted
    const shouldShowError = touchedFields.has(fieldName) || hasSubmitAttempted;
    
    if (!shouldShowError) {
      return false;
    }
    
    // Check for validation errors from the errors state first
    if (errors[fieldName]) {
      return true;
    }
    
    // Then check if required field is empty
    if (isFieldRequired(fieldName) && isFieldEmpty(value)) {
      return true;
    }
    
    return false;
  };

  // Add this function to handle field blur events
  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));
  };

  // Validate nodata value
  const validateNodata = (value) => {
    if (!value) return false;
    
    // Allow "nan" or "NAN" (case insensitive)
    if (value.toLowerCase() === "nan") return true;
    
    // Allow valid numbers (integers and floats)
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  // Validate flags (integers only)
  const validateFlags = (flagsArray) => {
    return flagsArray.every(flag => {
      if (flag.toLowerCase() === "nan") return true;
      return Number.isInteger(Number(flag)) && !isNaN(Number(flag));
    });
  };

  // Updated handleChange function
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    
    // Mark field as touched when user starts typing
    setTouchedFields(prev => new Set([...prev, name]));
    
    if (name === "legendType") {
      handleLegendTypeChange(e);
      return;
    }

    if (name === "country") {
      setIndexData((prev) => ({
        ...prev,
        country: finalValue ? finalValue : null,
      }));
      return;
    }
    
    if (name === "nodata") {
      // Update nodata value in indexData
      setIndexData((prev) => ({
        ...prev,
        [name]: finalValue,
      }));
      
      // Validate nodata
      if (finalValue && !validateNodata(finalValue)) {
        setErrors(prev => ({
          ...prev,
          nodata: { message: "Enter a valid number or 'nan' (nan = not a number)" }
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.nodata;
          return newErrors;
        });
      }
      
      // Update special legends for nodata
      updateSpecialLegendForNoData(finalValue);
    } else {
      setIndexData((prev) => ({
        ...prev,
        [name]: finalValue,
      }));
      
      // Clear error when user starts typing in required field
      if (isFieldRequired(name) && finalValue) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  const updateSpecialLegendForNoData = (nodataValue) => {
    // Remove existing nodata legend but keep flag legends
    const flagLegends = specialLegends.filter(legend => legend.type === "flag");
    
    // Create new nodata legend if value is not empty
    if (nodataValue) {
      const nodataLegend = {
        value: nodataValue,
        label: `No Data`,
        color: "#CCCCCC", // Default gray color for nodata
        type: "nodata" // Add a type identifier to distinguish nodata from flags
      };
      
      // Combine nodata and flag legends
      setSpecialLegends([nodataLegend, ...flagLegends]);
    } else {
      // If no nodata value, just keep flag legends
      setSpecialLegends(flagLegends);
    }
  };

  const handleLegendTypeChange = (e) => {
    const newLegendType = e.target.value;
    
    // Reset legends when changing types
    setIndexData((prev) => ({
      ...prev,
      legendType: newLegendType,
      legends: [], // Reset legends when changing type
    }));
    
    // Keep special legends (nodata and flags) but reset regular legends
    setSpecialLegends((prev) => 
      prev.filter(legend => legend.type === "nodata" || legend.type === "flag")
    );
  };


  const updateSpecialLegendsForFlags = (flagsArray) => {
    // Remove existing flag legends but keep nodata legend
    const nodataLegend = specialLegends.find(legend => legend.type === "nodata");
    
    // Create new legends for flags with unique identifiers to separate them from nodata
    const flagLegends = flagsArray.map((flag) => ({
      value: flag,
      label: `Flag`,
      color: "#FF0000", // Default red color for flags
      type: "flag" // Add a type identifier to distinguish flags from nodata
    }));
    
    // Combine nodata and flag legends
    const newSpecialLegends = [];
    if (nodataLegend) newSpecialLegends.push(nodataLegend);
    newSpecialLegends.push(...flagLegends);
    
    setSpecialLegends(newSpecialLegends);
  };

  
  const handleFlagsChange = (e) => {
    const value = e.target.value;
    setIndexData((prev) => ({
      ...prev,
      flagsInput: value,
    }));

    // Parse the input into an array of flags
    const flagsArray = value
      .split(",")
      .map((flag) => flag.trim())
      .filter((flag) => flag !== "");

    // Validate flags
    if (flagsArray.length > 0 && !validateFlags(flagsArray)) {
      setErrors(prev => ({
        ...prev,
        flags: { message: "Flags must be integers only" }
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.flags;
        return newErrors;
      });
    }

    // Update the flags array
    setIndexData((prev) => ({
      ...prev,
      flags: flagsArray,
    }));

    // Update special legends for flags
    updateSpecialLegendsForFlags(flagsArray);
  };

  // Add validation function
  const isValidFlag = (flag) => {
    return flag === "nan" || (!isNaN(Number(flag)) && Number.isInteger(Number(flag)));
  };

  const handleRangeDivisorChange = (e) => {
    const value = e.target.value;
    const divisor = value === "" ? "" : Number(value);

    setIndexData((prev) => ({
      ...prev,
      rangeDivisor: divisor,
    }));

    if (divisor > 0) {
      // updateRangeLegends();
    }
  };

  const handleMinValueChange = (e) => {
    const value = e.target.value;
    // Allow empty value or valid number
    const minValue = value === "" ? "" : value;
    setIndexData((prev) => ({
      ...prev,
      minValue: minValue,
    }));
    if (minValue !== "") {
      // updateRangeLegends();
    }
  };

  const handleMaxValueChange = (e) => {
    const value = e.target.value;
    console.log("MAX VAL:", value);
    // Allow empty value or valid number
    const maxValue = value === "" ? "" : value;
    setIndexData((prev) => ({
      ...prev,
      maxValue: maxValue,
    }));
    if (maxValue !== "") {
      // updateRangeLegends();
    }
  };

  const updateRangeLegends = () => {
    const { minValue, maxValue, rangeDivisor } = indexData;

    // Check if all values are valid numbers
    if (
      minValue === "" ||
      maxValue === "" ||
      rangeDivisor === "" ||
      isNaN(minValue) ||
      isNaN(maxValue) ||
      isNaN(rangeDivisor)
    ) {
      return;
    }

    const ranges = [{ value: "no data", color: "##4A4A4A" }];
    ranges.push({ value: `<=${minValue}`, color: legendColors[0] });

    let currentValue = Number(minValue);
    const maxVal = Number(maxValue);
    const step = Number(rangeDivisor);

    while (currentValue < maxVal) {
      const nextValue = Math.min(currentValue + step, maxVal);
      ranges.push({
        value: `>${currentValue}, <${nextValue}`,
        color: legendColors[ranges.length],
      });
      currentValue = nextValue;
    }

    ranges.push({ value: `>${maxVal}`, color: legendColors[ranges.length] });

    setIndexData((prev) => ({
      ...prev,
      legends: ranges,
    }));
  };

  const handleGradientColorChange = (index, color) => {
    const newColors = [...legendColors];
    newColors[index] = color;
    setLegendColors(newColors);

    // Update legends with new color
    const newLegends = indexData.legends.map((legend, i) =>
      i === index ? { ...legend, color } : legend
    );
    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
  };
  const handleColorChange = (index, color) => {
    const newLegends = [...indexData.legends];
    const legend = newLegends[index];
    
    if (Array.isArray(legend.value)) {
      // For range legends
      const rangeIndex = newLegends.findIndex(l => Array.isArray(l.value));
      const actualIndex = index - rangeIndex;
      const newColors = [...legendColors];
      newColors[actualIndex] = color;
      setLegendColors(newColors);
    }
    
    // Update the legend color
    newLegends[index] = {
      ...newLegends[index],
      color: color
    };

    setIndexData(prev => ({
      ...prev,
      legends: newLegends
    }));
  };
  const handleSpecialLegendColorChange = (index, color) => {
    const newSpecialLegends = [...specialLegends];
    newSpecialLegends[index] = {
      ...newSpecialLegends[index],
      color: color
    };
    setSpecialLegends(newSpecialLegends);
  };

  const handleSpecialLegendChange = (index, field, value) => {
    const newSpecialLegends = [...specialLegends];
    newSpecialLegends[index] = {
      ...newSpecialLegends[index],
      [field]: value,
    };
    setSpecialLegends(newSpecialLegends);
  };

  const handleAddRangeValue = () => {
    if (!indexData.minValue || !indexData.maxValue) return;

    const currentLegends = [...indexData.legends];
    const minValue = Number(indexData.minValue);
    const maxValue = Number(indexData.maxValue);

    // Get existing range legends
    const rangeLegends = currentLegends.filter(legend => Array.isArray(legend.value));
    const specialLegendsCount = currentLegends.length - rangeLegends.length;

    if (rangeLegends.length === 0) {
      // Add first range
      currentLegends.push({
        value: [minValue, null],
        label: "Range 1",
        color: legendColors[0],
      });
    } else {
      // Get the last range
      const lastRange = rangeLegends[rangeLegends.length - 1];
      
      // Add new range starting from last range's upper bound
      currentLegends.push({
        value: [lastRange.value[1] || null, null],
        label: `Range ${rangeLegends.length + 1}`,
        color: legendColors[rangeLegends.length] || "#000000",
      });
    }

    setIndexData(prev => ({
      ...prev,
      legends: currentLegends
    }));
  };

  const [validationTimeout, setValidationTimeout] = useState(null);

  const handleRangeValueChange = (index, boundIndex, value) => {
    const newLegends = [...indexData.legends];
    
    // Find the index of the first range (array value) legend
    const rangeIndex = newLegends.findIndex(legend => Array.isArray(legend.value));
    const rangeLegends = newLegends.filter(legend => Array.isArray(legend.value));
    
    // Get the actual index in the rangeLegends array
    const actualIndex = index - rangeIndex;
    
    if (!rangeLegends[actualIndex]) return;
    let newValue;

    if (value === '' || value === '-' || value === '.' || value === '-.') {
      newValue = [...rangeLegends[actualIndex].value];
      newValue[boundIndex] = value;
      
      rangeLegends[actualIndex] = {
        ...rangeLegends[actualIndex],
        value: newValue
      };
    } else {
      // Only convert to number if it's a valid numeric string
      newValue = [...rangeLegends[actualIndex].value];
      newValue[boundIndex] = /^-?\d*\.?\d*$/.test(value) ? value : rangeLegends[actualIndex].value[boundIndex];
      
      rangeLegends[actualIndex] = {
        ...rangeLegends[actualIndex],
        value: newValue
      };
    }

    // If this is not the last range and we're updating the upper bound,
    // update the next range's lower bound
    if (boundIndex === 1 && actualIndex < rangeLegends.length - 1) {
      rangeLegends[actualIndex + 1].value[0] = value;
    }

    // Reconstruct the legends array
    const updatedLegends = [
      ...newLegends.slice(0, rangeIndex),
      ...rangeLegends
    ];

    setIndexData(prev => ({
      ...prev,
      legends: updatedLegends
    }));

    // Validation
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    const newTimeout = setTimeout(() => {
      if (value === '') {
        // Clear error when field is empty
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`range_${index}_${boundIndex}`];
          return newErrors;
        });
        return;
      }

      
      
      const numValue = Number(value);
      if (isNaN(numValue)) return;

      const errors = {};
      let hasError = false;
      const newErrors = { ...errors };
      
      // Validate range bounds
      if (numValue < Number(indexData.minValue) || numValue > Number(indexData.maxValue)) {
        newErrors[`range_${index}_${boundIndex}`] = {
          message: `Value must be between ${indexData.minValue} and ${indexData.maxValue}`
        };
        hasError = true;
      }
      
      // Validate lower/upper bound relationship
      if (boundIndex === 0 && newValue[1] && numValue >= Number(newValue[1])) {
        newErrors[`range_${index}_${boundIndex}`] = {
          message: `Lower bound must be less than upper bound (${newValue[1]})`
        };
        hasError = true;
      } else if (boundIndex === 1 && newValue[0] && numValue <= Number(newValue[0])) {
        newErrors[`range_${index}_${boundIndex}`] = {
          message: `Upper bound must be greater than lower bound (${newValue[0]})`
        };
        hasError = true;
      }

      if (!hasError) {
        delete newErrors[`range_${index}_${boundIndex}`];
      }

      setErrors(newErrors);
    }, 500);

    setValidationTimeout(newTimeout);
  };

  const renderGradientLegends = () => (
    <div style={{ width: "100%" }}>
      {errors.legends && (
        <Typography
          color="error"
          sx={{
            mb: 2,
            whiteSpace: "pre-line", // This preserves line breaks
            fontFamily: "inherit", // Maintains consistent font
          }}
        >
          {errors.legends.message}
        </Typography>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <TextField
            type="text"
            value={indexData.minValue || ""}
            onChange={(e) => handleAddGradientMinMax("minValue", e)}
            onBlur={() => handleFieldBlur("minValue")}
            label="Minimum Possible Value *"
            size="small"
            error={getFieldError("minValue", indexData.minValue)}
            helperText={errors.minValue?.message}
            style={{ width: "250px" }}
          />
          <TextField
            type="text"
            value={indexData.maxValue || ""}
            onChange={(e) => handleAddGradientMinMax("maxValue", e)}
            onBlur={() => handleFieldBlur("maxValue")}
            label="Maximum Possible Value *"
            size="small"
            error={getFieldError("maxValue", indexData.maxValue)}
            helperText={errors.maxValue?.message}
            style={{ width: "250px" }}
          />
        </div>
        {indexData.legends.length > 0 && (
          <Typography sx={{ fontStyle: "italic" }} variant="body2">
            Click on the color box to change color
          </Typography>
        )}
        {/* Render special legends first */}
      {specialLegends.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            No Data and Flags
          </Typography>
          {specialLegends.map((legend, index) => (
            <div
              key={`special-${index}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                backgroundColor: "#f5f5f5",
                padding: "8px 16px",
                borderRadius: "4px",
                marginBottom: "8px",
              }}
            >
              <TextField
        value={legend.value}
        label="Value"
        size="small"
        type="text"
        style={{ width: '120px' }}
        disabled={true}
      />
      <TextField
        value={legend.label || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label", e.target.value)}
        label="Label *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label}
        disabled={false}
      />
      <TextField
        value={legend.label_ar || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label_ar", e.target.value)}
        label="Label (AR) *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label_ar}
        disabled={false}
      />
              <input
                type="color"
                value={legend.color}
                onChange={(e) => handleSpecialLegendColorChange(index, e.target.value)}
                style={{ width: 24, height: 24, padding: 0, border: "none" }}
              />
            </div>
          ))}
          <div 
            style={{ 
              height: '1px', 
              backgroundColor: '#e0e0e0', 
              margin: '24px 0' 
            }} 
          />
        </>
      )}
        {indexData.legends.map((legend, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              backgroundColor: "#f5f5f5",
              padding: "8px 16px",
              borderRadius: "4px",
            }}
          >
            <TextField
              value={legend.value || ""}
              onChange={(e) =>
                handleGradientChange(index, "value", e.target.value)
              }
              label="Value *"
              size="small"
              style={{ width: "150px" }}
              error={!!errors[`gradient_${index}`] || !legend.value}
              helperText={errors[`gradient_${index}`]?.message}
            />
            <TextField
              value={legend.label || ""}
              onChange={(e) =>
                handleGradientChange(index, "label", e.target.value)
              }
              label="Label *"
              size="small"
              style={{ width: "200px" }}
              error={!legend.label}
            />
            <TextField
              value={legend.label_ar || ""}
              onChange={(e) => handleGradientChange(index, "label_ar", e.target.value)}
              label="Label (AR) *"
              size="small"
              style={{ width: "200px" }}
              error={!legend.label_ar}
            />
            <input
              type="color"
              value={legend.color}
              onChange={(e) => handleGradientColorChange(index, e.target.value)}
              style={{
                width: 24,
                height: 24,
                padding: 0,
                border: "none",
              }}
            />
            <IconButton
              onClick={() => handleRemoveGradient(index)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ))}
        {indexData.legends.length > 0 && (
          <Typography sx={{ fontStyle: "italic" }} variant="body2" gutterBottom>
            The values should not be scaled
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleAddGradient}
          style={{ marginBottom: 20, width: "150px" }}
        >
          Add Value
        </Button>
      </div>
    </div>
  );

  const renderRangeLegends = () => (
    <div style={{ width: "100%" }}>
        {errors.legends && (
        <Typography
          color="error"
          sx={{
            mb: 2,
            whiteSpace: "pre-line",
            fontFamily: "inherit",
          }}
        >
          {errors.legends.message}
        </Typography>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <TextField
          type="text"
          value={indexData.minValue || ""}
          onChange={(e) => handleAddMinMax("minValue", e)}
          onBlur={() => handleFieldBlur("minValue")}
          label="Lower Possible Value *"
          size="small"
          error={getFieldError("minValue", indexData.minValue)}
          helperText={errors.minValue?.message}
          style={{ width: "250px" }}
        />
        <TextField
          type="text"
          value={indexData.maxValue || ""}
          onChange={(e) => handleAddMinMax("maxValue", e)}
          onBlur={() => handleFieldBlur("maxValue")}
          label="Upper Possible Value *"
          size="small"
          error={getFieldError("maxValue", indexData.maxValue)}
          helperText={errors.maxValue?.message}
          style={{ width: "250px" }}
        />
      </div>

      {/* Render special legends first */}
      {specialLegends.length > 0 && (
         <>
         <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
           No Data and Flags
         </Typography>
      {specialLegends.map((legend, index) => (
        <div
          key={`special-${index}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            backgroundColor: "#f5f5f5",
            padding: "8px 16px",
            borderRadius: "4px",
            marginBottom: "8px",
          }}
        >
          <TextField
        value={legend.value}
        label="Value"
        size="small"
        type="text"
        style={{ width: '120px' }}
        disabled={true}
      />
      <TextField
        value={legend.label || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label", e.target.value)}
        label="Label *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label}
        disabled={false}
      />
      <TextField
        value={legend.label_ar || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label_ar", e.target.value)}
        label="Label (AR) *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label_ar}
        disabled={false}
      />
          <input
            type="color"
            value={legend.color}
            onChange={(e) => handleSpecialLegendColorChange(index, e.target.value)}
            style={{ width: 24, height: 24, padding: 0, border: "none" }}
          />
        </div>
      ))}
      <div 
            style={{ 
              height: '1px', 
              backgroundColor: '#e0e0e0', 
              margin: '24px 0' 
            }} 
          />
        </>
    )}

       {/* Regular range legends */}
       {indexData.legends.filter(legend => Array.isArray(legend.value)).map((legend, index) => (
        <div
          key={`range-${index}`}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            backgroundColor: "#f5f5f5",
            padding: "8px 16px",
            borderRadius: "4px",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <TextField
              value={legend.value[0] || ""}
              onChange={(e) => handleRangeValueChange(index, 0, e.target.value)}
              label="Lower"
              size="small"
              type="text"
              style={{ width: '120px' }}
              error={!!errors[`range_${index}_0`]}
              disabled={true}
            />
            {errors[`range_${index}_0`] && (
              <Typography variant="caption" color="error" sx={{ pl: 1 }}>
                {errors[`range_${index}_0`].message}
              </Typography>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <TextField
              value={legend.value[1] || ""}
              onChange={(e) => handleRangeValueChange(index, 1, e.target.value)}
              label="Upper"
              size="small"
              type="text"
              style={{ width: '120px' }}
              error={!!errors[`range_${index}_1`]}
            />
            {errors[`range_${index}_1`] && (
              <Typography variant="caption" color="error">
                {errors[`range_${index}_1`].message}
              </Typography>
            )}
          </div>
          <TextField
            value={legend.label || ""}
            onChange={(e) => handleRangeChange(index, "label", e.target.value)}
            label="Label *"
            size="small"
            style={{ width: "200px" }}
            error={!legend.label}
          />
          <TextField
            value={legend.label_ar || ""}
            onChange={(e) => handleRangeChange(index, "label_ar", e.target.value)}
            label="Label (AR) *"
            size="small"
            style={{ width: "200px" }}
            error={!legend.label_ar}
          />
          <input
            type="color"
            value={legend.color}
            onChange={(e) => handleColorChange(index, e.target.value)}
            style={{ width: 24, height: 24, padding: 0, border: "none" }}
          />
          <IconButton
            onClick={() => handleRemoveRange(index)}
            size="small"
            color="error"
            disabled={index === 0}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ))}

{indexData.legends.length > 0 && 
       indexData.maxValue && 
       (!indexData.legends[indexData.legends.length - 1].value[1] || 
        Number(indexData.legends[indexData.legends.length - 1].value[1]) !== Number(indexData.maxValue)) && (
        <Typography 
          sx={{ 
            fontStyle: "italic", 
            color: "warning.main", 
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} 
          variant="body2"
        >
          ⚠️ Note: The upper value of the last range must equal the upper possible value ({indexData.maxValue})
        </Typography>
      )}
           <Button
        variant="contained"
        onClick={handleAddRangeValue}
        style={{ marginTop: 10, width: "150px" }}
        disabled={
          !indexData.minValue ||
          !indexData.maxValue ||
          (indexData.legends.length > 0 &&
            indexData.legends.filter(legend => Array.isArray(legend.value)).some(legend => 
              legend.value[1] === null || 
              legend.value[1] === '' ||
              Number(legend.value[1]) === Number(indexData.maxValue)
            ))
        }
      >
        Add Value
      </Button>
    </div>
  );

  // Updated handleSubmit function
  const handleSubmit = async () => {
    setHasSubmitAttempted(true); // This will show all validation errors
    
    const newErrors = {};
    const requiredFields = [
      "name",
      "name_ar",
      "type_id",
      "scale",
      "offset",
      "nodata",
      "resolution",
      "provider",
      "provider_ar",
      "description",
      "description_ar",
      "source",
      "source_ar",
      "citation",
      "citation_ar",
      "methodology",
      "methodology_ar",
      "terms",
      "terms_ar",
      "temporal_resolution",
      "temporal_resolution_ar",
      "legends",
    ];

    // Add minValue and maxValue for ranges and gradient types
    if (indexData.legendType === "ranges" || indexData.legendType === "gradient") {
      requiredFields.push("minValue", "maxValue");
    }

    // Validate required fields
    requiredFields.forEach((field) => {
      let value = indexData[field];

      // Special handling for legends array
      if (field === "legends" && (!value || value.length === 0)) {
        newErrors[field] = {
          message: "At least one legend entry is required",
        };
      } else {
        const error = validateField(field, value);
        if (error.message) {
          newErrors[field] = error;
        }
      }
    });

    // Validate nodata
    if (indexData.nodata && !validateNodata(indexData.nodata)) {
      newErrors.nodata = { message: "Enter a valid number or 'nan' (nan = not a number)" };
    }

    // Validate flags
    if (indexData.flags.length > 0 && !validateFlags(indexData.flags)) {
      newErrors.flags = { message: "Flags must be integers only" };
    }

    // Validate legend fields
    indexData.legends.forEach((legend, index) => {
      if (!legend.value && legend.value !== 0) {
        newErrors[`legend_value_${index}`] = { message: "Value is required" };
      }
      if (!legend.label) {
        newErrors[`legend_label_${index}`] = { message: "Label is required" };
      }
      if (!legend.label_ar) {
        newErrors[`legend_label_ar_${index}`] = { message: "Label (AR) is required" };
      }
    });

    // Validate special legends
    specialLegends.forEach((legend, index) => {
      if (!legend.label) {
        newErrors[`special_legend_label_${index}`] = { message: "Label is required" };
      }
      if (!legend.label_ar) {
        newErrors[`special_legend_label_ar_${index}`] = { message: "Label (AR) is required" };
      }
    });

    // Use pre-calculated legend errors
    if (legendErrors.length > 0) {
      newErrors.legends = {
        message: legendErrors.map((err) => `• ${err}`).join("\n"),
      };
    }

    if (indexData.legendType === 'ranges' && indexData.legends.length > 0) {
        const lastRange = indexData.legends[indexData.legends.length - 1];
        if (!lastRange.value[1] || Number(lastRange.value[1]) !== Number(indexData.maxValue)) {
            const rangeError = `• The last range's upper value (${lastRange.value[1] || 'empty'}) must equal the upper possible value (${indexData.maxValue})`;
            newErrors.legends = {
                message: newErrors.legends 
                    ? `${newErrors.legends.message}\n${rangeError}`
                    : rangeError
            };
        }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setSubmitLoading(true);
       try {
      // Organize legends by type
      const organizedLegends = {
        nodata: specialLegends.filter(legend => legend.value === (indexData.nodata === "nan" ? "nan" : indexData.nodata)),
        flags: specialLegends.filter(legend => 
          indexData.flags?.includes(legend.value === "nan" ? "nan" : legend.value)),
        others: indexData.legends.map(legend => {
          if (Array.isArray(legend.value)) {
            // Convert range values to numbers
            return {
              ...legend,
              value: legend.value.map(v => v === '' ? null : Number(v))
            };
          } else if (indexData.legendType === 'gradient') {
            // Convert gradient values to numbers
            return {
              ...legend,
              value: legend.value === '' ? null : Number(legend.value)
            };
          }
          return legend;
        })
      };

      console.log("special legends:", specialLegends)

      console.log("legends:", specialLegends.filter(legend => legend.type === 'nodata' && legend.value === (indexData.nodata === 'nan' ? 'nan' : indexData.nodata)))

      // Convert string values to numbers for submission
      const processedData = {
        ...indexData,
        legends: organizedLegends,
        scale: Number(indexData.scale),
        offset: Number(indexData.offset),
        nodata: indexData.nodata === "nan" ? "nan" : Number(indexData.nodata),
        minValue: indexData.minValue ? Number(indexData.minValue) : null,
        maxValue: indexData.maxValue ? Number(indexData.maxValue) : null,
        rangeDivisor: indexData.rangeDivisor ? Number(indexData.rangeDivisor) : null
      };

      await onAdd(processedData);
      onClose();
    } catch (error) {
      console.error("Error adding index:", error);
        
        // Use the getErrorMessage helper to get localized error message
        const errorMessage = getErrorMessage(error, 
          i18n.language === 'ar' ? 'فشل في إضافة المؤشر. يرجى المحاولة مرة أخرى.' : 'Failed to add index. Please try again.'
        );
        
        setErrors(prev => ({
          ...prev,
          submit: { message: errorMessage }
        }));
    } finally {
      setSubmitLoading(false); // Add this line
    }
  }
  };

  const handleAddCategory = () => {
    // Add new empty category
    const newLegends = [...indexData.legends];
    newLegends.push({
      value: "",
      label: "",
      color: legendColors[newLegends.length] || "#000000",
    });

    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
    console.log("LEGENDS", indexData.legends)
    console.log("SPECIAL LEGENDS", specialLegends)
  };

  const handleCategoryChange = (index, field, value) => {
    const newLegends = indexData.legends.map((legend, i) => {
      if (i === index) {
        // If it's the value field, validate based on legend type
        if (field === "value") {
          // For category legends, only allow integers (no floats or "nan")
          if (value === "" || /^-?\d+$/.test(value)) {
            return { ...legend, [field]: value };
          }
          // Keep existing value if invalid input
          return legend;
        }
        return { ...legend, [field]: value };
      }
      return legend;
    });

    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
  };

  const handleCategoryColorChange = (index, color) => {
    const newLegends = indexData.legends.map((legend, i) =>
      i === index ? { ...legend, color } : legend
    );
    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
  };

  const handleRemoveCategory = (index) => {
    setIndexData((prev) => ({
      ...prev,
      legends: prev.legends.filter((_, i) => i !== index),
    }));
  };

  const renderCategoryLegends = () => (
    <div style={{ width: "100%" }}>
      {errors.legends && (
        <Typography
          color="error"
          sx={{
            mb: 2,
            whiteSpace: "pre-line", // This preserves line breaks
            fontFamily: "inherit", // Maintains consistent font
          }}
        >
          {errors.legends.message}
        </Typography>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {indexData.legends.length > 0 && (
          <Typography sx={{ fontStyle: "italic" }} variant="body2">
            Click on the color box to change color
          </Typography>
        )}
        {/* Render special legends first */}
      {specialLegends.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            No Data and Flags
          </Typography>
          {specialLegends.map((legend, index) => (
            <div
              key={`special-${index}`}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                backgroundColor: "#f5f5f5",
                padding: "8px 16px",
                borderRadius: "4px",
                marginBottom: "8px",
              }}
            >
              <TextField
        value={legend.value}
        label="Value"
        size="small"
        type="text"
        style={{ width: '120px' }}
        disabled={true}
      />
      <TextField
        value={legend.label || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label", e.target.value)}
        label="Label *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label}
        disabled={false}
      />
      <TextField
        value={legend.label_ar || ""}
        onChange={(e) => handleSpecialLegendChange(index, "label_ar", e.target.value)}
        label="Label (AR) *"
        size="small"
        style={{ width: "200px" }}
        error={!legend.label_ar}
        disabled={false}
      />
              <input
                type="color"
                value={legend.color}
                onChange={(e) => handleSpecialLegendColorChange(index, e.target.value)}
                style={{ width: 24, height: 24, padding: 0, border: "none" }}
              />
            </div>
          ))}
          <div 
            style={{ 
              height: '1px', 
              backgroundColor: '#e0e0e0', 
              margin: '24px 0' 
            }} 
          />
        </>
      )}
      {indexData.nodata && !specialLegends.some(legend => 
        (legend.value === "nan" && indexData.nodata === "nan") || 
        (legend.value === indexData.nodata)
      ) && (
        <Typography 
          sx={{ 
            fontStyle: "italic", 
            color: "warning.main", 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} 
          variant="body2"
        >
          ⚠️ Note: No Data value ({indexData.nodata}) must be represented in the legend
        </Typography>
      )}
        {indexData.legends.map((legend, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              backgroundColor: "#f5f5f5",
              padding: "8px 16px",
              borderRadius: "4px",
            }}
          >
            <TextField
              value={legend.value || ""}
              onChange={(e) =>
                handleCategoryChange(index, "value", e.target.value)
              }
              label="Value *"
              size="small"
              style={{ width: "150px" }}
              error={!legend.value && legend.value !== 0}
            />
            <TextField
              value={legend.label || ""}
              onChange={(e) =>
                handleCategoryChange(index, "label", e.target.value)
              }
              label="Label *"
              size="small"
              style={{ width: "200px" }}
              error={!legend.label}
            />
            <TextField
              value={legend.label_ar || ""}
              onChange={(e) => handleCategoryChange(index, "label_ar", e.target.value)}
              label="Label (AR) *"
              size="small"
              style={{ width: "200px" }}
              error={!legend.label_ar}
            />
            <input
              type="color"
              value={legend.color}
              onChange={(e) => handleCategoryColorChange(index, e.target.value)}
              style={{
                width: 24,
                height: 24,
                padding: 0,
                border: "none",
              }}
            />
            <IconButton
              onClick={() => handleRemoveCategory(index)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ))}
        {indexData.legends.length > 0 && (
          <Typography sx={{ fontStyle: "italic" }} variant="body2" gutterBottom>
            The values should not be scaled
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={handleAddCategory}
          style={{ marginBottom: 20, width: "150px" }}
        >
          Add Value
        </Button>
      </div>
    </div>
  );

  const handleAddGradient = () => {
    const newLegends = [...indexData.legends];
    newLegends.push({
      value: "",
      label: "",
      color: legendColors[newLegends.length] || "#000000",
    });

    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
    console.log("LEGENDS", indexData.legends)
    console.log("SPECIAL LEGENDS", specialLegends)
  };

  const handleAddGradientMinMax = (field, e) => {
    const error = validateField(field, e.target.value);
    if (error.message) {
      setErrors((prev) => ({
        ...prev,
        [field]: error,
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    const value = e.target.value;
    setIndexData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleAddMinMax = (field, e) => {
    const error = validateField(field, e.target.value);
    const value = e.target.value;

    // Clear or set error based on validation
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error.message) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });


    if (indexData.legends.length > 0) {
      const newLegends = [...indexData.legends];
      const firstRangeIndex = newLegends.findIndex(legend => Array.isArray(legend.value));
      
      if (field === 'maxValue') {
        // Update the last range's upper value
        newLegends[newLegends.length - 1].value[1] = '';
      } else if (field === 'minValue' && firstRangeIndex !== -1) {
        // Update the first range's lower value
        newLegends[firstRangeIndex].value[0] = value;
      }
      
      setIndexData(prev => ({
        ...prev,
        [field]: value,
        legends: newLegends
      }));
    } else {
      setIndexData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleGradientChange = (index, field, value) => {
    const newLegends = [...indexData.legends];
    
    if (field === 'value') {
      // Allow all numeric inputs but validate
      if (value === '' || value === '-' || value === '.' || value === '-.') {
        newLegends[index] = {
          ...newLegends[index],
          [field]: value,
        };
        // Clear error when empty
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`gradient_${index}`];
          return newErrors;
        });
      } else if (/^-?\d*\.?\d*$/.test(value)) {
        const numValue = Number(value);
        const minValue = Number(indexData.minValue);
        const maxValue = Number(indexData.maxValue);
        
        newLegends[index] = {
          ...newLegends[index],
          [field]: value,
        };

        if (!isNaN(numValue) && !isNaN(minValue) && !isNaN(maxValue)) {
          if (numValue < minValue || numValue > maxValue) {
            setErrors(prev => ({
              ...prev,
              [`gradient_${index}`]: {
                message: `Value must be between ${indexData.minValue} and ${indexData.maxValue}`
              }
            }));
          } else {
            // Clear error when valid
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[`gradient_${index}`];
              return newErrors;
            });
          }
        }
      }
    } else {
      // For non-value fields (like label), update normally
      newLegends[index] = {
        ...newLegends[index],
        [field]: value,
      };
    }

    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
  };

  const handleRangeChange = (index, field, value) => {
    const newLegends = indexData.legends.map((legend, i) =>
      i === index ? { ...legend, [field]: value } : legend
    );
    setIndexData((prev) => ({
      ...prev,
      legends: newLegends,
    }));
  };

  const handleRemoveGradient = (index) => {
    setIndexData((prev) => ({
      ...prev,
      legends: prev.legends.filter((_, i) => i !== index),
    }));
  };
  const handleRemoveRange = (index) => {
    setIndexData((prev) => ({
      ...prev,
      legends: prev.legends.filter((_, i) => i !== index),
    }));
  };

  const validateLegendsContainSpecialValues = () => {
    const errors = [];

    // Check if nodata value is represented
    // if (indexData.nodata) {
    //   const nodataValue = indexData.nodata === "nan" ? "nan" : Number(indexData.nodata);
    //   const hasNoData = specialLegends.some(legend => 
    //     (legend.value === "nan" && nodataValue === "nan") || 
    //     (typeof legend.value === "number" && legend.value === nodataValue)
    //   );
      
    //   if (!hasNoData) {
    //     errors.push(`No Data value (${indexData.nodata}) must be represented in the legend`);
    //   }
    // }

    // Check if flag values are represented
    // if (indexData.flags && indexData.flags.length > 0) {
    //   indexData.flags.forEach(flag => {
    //     const flagValue = flag === "nan" ? "nan" : Number(flag);
    //     const hasFlag = specialLegends.some(legend => 
    //       (legend.value === "nan" && flagValue === "nan") || 
    //       (typeof legend.value === "number" && legend.value === flagValue)
    //     );
        
    //     if (!hasFlag) {
    //       errors.push(`Flag value (${flag}) must be represented in the legend`);
    //     }
    //   });
    // }

    return errors;
  };

  return (
    <Dialog maxWidth="lg" open={open} onClose={submitLoading ? undefined : onClose} fullScreen={isMobile} sx={{ width: "100vw", sm:"auto" }}>
      <DialogTitle sx={{ pt: isMobile ? 'max(16px, env(safe-area-inset-top))' : undefined }}>{t('Map.add_index')}</DialogTitle>
      <DialogContent dividers="true">
        {errors.submit && (
          <Typography
            color="error"
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #ffcdd2'
            }}
          >
            {errors.submit.message}
          </Typography>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            padding: "20px",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {/* Basic Information - English */}
        <div>
          <Typography variant="h6" gutterBottom>
            Basic Information (English)
          </Typography>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <TextField
              name="name"
              label="Name (EN) *"
              style={{ width: "300px" }}
              value={indexData.name || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("name")}
              error={getFieldError("name", indexData.name)}
              helperText={errors.name?.message || `${indexData.name?.length || 0}/40`}
              inputProps={{ maxLength: 40 }}
            />
            <FormControl style={{ width: "300px" }} error={getFieldError("type_id", indexData.type_id)}>
              <InputLabel id="type-select-label">Type *</InputLabel>
              <Select
                labelId="type-select-label"
                name="type_id"
                value={indexData.type_id || ""}
                onChange={handleChange}
                onBlur={() => handleFieldBlur("type_id")}
              >
                {Array.isArray(types) &&
                  types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
              </Select>
              {getFieldError("type_id", indexData.type_id) && <FormHelperText>Type is required</FormHelperText>}
            </FormControl>
            <FormControl style={{ width: "300px" }} error={getFieldError("country", indexData.country)}>
              <InputLabel id="country-select-label">{t('Map.country')}</InputLabel>
              <Select
                labelId="country-select-label"
                name="country"
                value={indexData.country || ""}
                onChange={handleChange}
                onBlur={() => handleFieldBlur("country")}
              >
                <MenuItem value="">
                  <em>{t('CustomIndex.all_countries')}</em>
                </MenuItem>
                {Object.keys(t('countries', { returnObjects: true })).map((countryKey) => (
                  <MenuItem key={countryKey} value={countryKey}>
                    {t(`countries.${countryKey}`)}
                  </MenuItem>
                ))}
              </Select>
              {getFieldError("country", indexData.country) && <FormHelperText>Country is required</FormHelperText>}
            </FormControl>
            <TextField
              name="provider"
              label="Provider (EN) *"
              style={{ width: "300px" }}
              value={indexData.provider || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("provider")}
              error={getFieldError("provider", indexData.provider)}
              helperText={errors.provider?.message || `${indexData.provider?.length || 0}/120`}
              inputProps={{ maxLength: 120 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={indexData.custom}
                  onChange={handleChange}
                  name="custom"
                  color="primary"
                />
              }
              label={t('CustomIndex.type_custom')}
              sx={{ ml: 1, height: 40, alignSelf: 'center' }}
            />
            <TextField
              name="description"
              label="Description (EN) *"
              style={{ width: "920px" }}
              value={indexData.description || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("description")}
              error={getFieldError("description", indexData.description)}
              helperText={errors.description?.message || `${indexData.description?.length || 0}/512`}
              inputProps={{ maxLength: 512 }}
              multiline
              rows={10}
            />
          </div>
        </div>

        {/* Basic Information - Arabic */}
        <div style={{ marginTop: "30px" }}>
          <Typography variant="h6" gutterBottom>
            البيانات الأساسية (العربية)
          </Typography>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <TextField
              name="name_ar"
              label="الاسم (AR) *"
              style={{ width: "300px" }}
              value={indexData.name_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("name_ar")}
              error={getFieldError("name_ar", indexData.name_ar)}
              helperText={errors.name_ar?.message || `${indexData.name_ar?.length || 0}/40`}
              inputProps={{ maxLength: 40 }}
            />
            <TextField
              name="provider_ar"
              label="المزود (AR) *"
              style={{ width: "300px" }}
              value={indexData.provider_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("provider_ar")}
              error={getFieldError("provider_ar", indexData.provider_ar)}
              helperText={errors.provider_ar?.message || `${indexData.provider_ar?.length || 0}/120`}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              name="description_ar"
              label="الوصف (AR) *"
              style={{ width: "920px" }}
              value={indexData.description_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("description_ar")}
              error={getFieldError("description_ar", indexData.description_ar)}
              helperText={errors.description_ar?.message || `${indexData.description_ar?.length || 0}/512`}
              inputProps={{ maxLength: 512 }}
              multiline
              rows={10}
            />
          </div>
        </div>


          {/* Data Properties Section */}
          <div>
            <Typography variant="h6" gutterBottom>
              Data Properties
            </Typography>
            {/* Main container for rows */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* Row for non-translated fields */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <TextField
                  name="scale"
                  label="Scale *"
                  type="number"
                  style={{ width: "300px" }}
                  value={indexData.scale || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("scale")}
                  error={getFieldError("scale", indexData.scale)}
                  helperText={errors.scale?.message}
                />
                <TextField
                  name="offset"
                  label="Offset *"
                  type="number"
                  style={{ width: "300px" }}
                  value={indexData.offset !== null ? indexData.offset : ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("offset")}
                  error={getFieldError("offset", indexData.offset)}
                  helperText={errors.offset?.message}
                />
                <TextField
                  name="resolution"
                  label="Pixel Resolution *"
                  placeholder="300 meters, 0.005 deg, or 30*25 meters^2"
                  style={{ width: "300px" }}
                  value={indexData.resolution || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("resolution")}
                  error={getFieldError("resolution", indexData.resolution)}
                  helperText={
                    errors.resolution?.message ||
                    `${indexData.resolution?.length || 0}/40`
                  }
                  inputProps={{ maxLength: 40 }}
                />
                <TextField
                  name="nodata"
                  label="No Data Value *"
                  style={{ width: "300px" }}
                  value={indexData.nodata || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("nodata")}
                  error={getFieldError("nodata", indexData.nodata) || (indexData.nodata && !validateNodata(indexData.nodata))}
                  helperText={errors.nodata?.message || "Enter numbers or nan (nan = not a number)"}
                />
                <TextField
                  name="flags"
                  label="Flags"
                  style={{ width: "300px" }}
                  value={indexData.flagsInput || ""}
                  onChange={handleFlagsChange}
                  helperText="Enter integers separated by commas (e.g., 8, 32, -324)"
                  error={!!errors.flags}
                  placeholder="8, 32, -324"
                />
              </div>

              {/* Row for Unit fields (EN/AR) */}
              <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                <TextField
                  name="unit"
                  label="Unit (EN)"
                  style={{ width: "300px" }}
                  value={indexData.unit || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("unit")}
                  error={getFieldError("unit", indexData.unit)}
                  helperText={errors.unit?.message}
                />
                <TextField
                  name="unit_ar"
                  label="الوحدة (AR)"
                  style={{ width: "300px" }}
                  value={indexData.unit_ar || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("unit_ar")}
                  error={getFieldError("unit_ar", indexData.unit_ar)}
                  helperText={errors.unit_ar?.message}
                />
              </div>

              {/* Row for Temporal Resolution fields (EN/AR) */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <TextField
                  name="temporal_resolution"
                  label="Temporal Resolution (EN) *"
                  type="text"
                  style={{ width: "300px" }}
                  value={indexData.temporal_resolution || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("temporal_resolution")}
                  error={getFieldError("temporal_resolution", indexData.temporal_resolution)}
                  helperText={errors.temporal_resolution?.message}
                />
                <TextField
                  name="temporal_resolution_ar"
                  label="الدقة الزمنية (AR) *"
                  style={{ width: "300px" }}
                  value={indexData.temporal_resolution_ar || ""}
                  onChange={handleChange}
                  onBlur={() => handleFieldBlur("temporal_resolution_ar")}
                  error={getFieldError("temporal_resolution_ar", indexData.temporal_resolution_ar)}
                  helperText={errors.temporal_resolution_ar?.message}
                />
              </div>
            </div>
          </div>

          {/* Legend Configuration Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <Typography variant="h6" gutterBottom>
              Legend Configuration
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'warning.main',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              ⚠️ {t('Map.legend_scale_warning') || 'Make sure values are not scaled or offsetted'}
            </Typography>
            <FormControl style={{ width: "300px", marginBottom: "20px" }}>
              <InputLabel>Legend Type *</InputLabel>
              <Select
                name="legendType"
                value={indexData.legendType}
                onChange={handleChange}
              >
                <MenuItem value="ranges">Ranges</MenuItem>
                <MenuItem value="gradient">Gradient</MenuItem>
                <MenuItem value="category">Category</MenuItem>
              </Select>
            </FormControl>
            {indexData.legendType === "ranges"
              ? renderRangeLegends()
              : indexData.legendType === "gradient"
              ? renderGradientLegends()
              : renderCategoryLegends()}
          </div>

           {/* Metadata Section - English */}
          <div>
            <Typography variant="h6" gutterBottom>
            Metadata (English)
            </Typography>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <TextField
                name="source"
                label="Source (EN) *"
                style={{ width: "920px" }}
                value={indexData.source || ""}
                onChange={handleChange}
                onBlur={() => handleFieldBlur("source")}
                error={getFieldError("source", indexData.source)}
                helperText={errors.source?.message || `${indexData.source?.length || 0}/120`}
                inputProps={{ maxLength: 120 }}
                multiline
                rows={5}
              />
              <TextField
            name="citation"
            label="Citation (EN) *"
            style={{ width: "920px" }}
            value={indexData.citation || ""}
            onChange={handleChange}
            onBlur={() => handleFieldBlur("citation")}
            error={getFieldError("citation", indexData.citation)}
            helperText={errors.citation?.message || `${indexData.citation?.length || 0}/512`}
            inputProps={{ maxLength: 512 }}
            multiline
            rows={5}
          />
          <TextField
            name="methodology"
            label="Methodology (EN) *"
            style={{ width: "920px" }}
            value={indexData.methodology || ""}
            onChange={handleChange}
            onBlur={() => handleFieldBlur("methodology")}
            error={getFieldError("methodology", indexData.methodology)}
            helperText={errors.methodology?.message || `${indexData.methodology?.length || 0}/512`}
            inputProps={{ maxLength: 512 }}
            multiline
            rows={5}
          />
          <TextField
            name="terms"
            label="Terms (EN) *"
            style={{ width: "920px" }}
            value={indexData.terms || ""}
            onChange={handleChange}
            onBlur={() => handleFieldBlur("terms")}
            error={getFieldError("terms", indexData.terms)}
            helperText={errors.terms?.message || `${indexData.terms?.length || 0}/512`}
            inputProps={{ maxLength: 512 }}
            multiline
            rows={5}
          />
            </div>
          </div>
             {/* Metadata Section - Arabic */}
        <div style={{ marginTop: "30px" }}>
          <Typography variant="h6" gutterBottom>
            البيانات الوصفية (العربية)
          </Typography>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <TextField
              name="source_ar"
              label="المصدر (AR) *"
              style={{ width: "920px" }}
              value={indexData.source_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("source_ar")}
              error={getFieldError("source_ar", indexData.source_ar)}
              helperText={errors.source_ar?.message || `${indexData.source_ar?.length || 0}/120`}
              inputProps={{ maxLength: 120 }}
              multiline
              rows={5}
            />
            <TextField
              name="citation_ar"
              label="الاقتباس (AR) *"
              style={{ width: "920px" }}
              value={indexData.citation_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("citation_ar")}
              error={getFieldError("citation_ar", indexData.citation_ar)}
              helperText={errors.citation_ar?.message || `${indexData.citation_ar?.length || 0}/512`}
              inputProps={{ maxLength: 512 }}
              multiline
              rows={5}
            />
            <TextField
              name="methodology_ar"
              label="المنهجية (AR) *"
              style={{ width: "920px" }}
              value={indexData.methodology_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("methodology_ar")}
              error={getFieldError("methodology_ar", indexData.methodology_ar)}
              helperText={errors.methodology_ar?.message || `${indexData.methodology_ar?.length || 0}/512`}
              inputProps={{ maxLength: 512 }}
              multiline
              rows={5}
            />
            <TextField
              name="terms_ar"
              label="الشروط (AR) *"
              style={{ width: "920px" }}
              value={indexData.terms_ar || ""}
              onChange={handleChange}
              onBlur={() => handleFieldBlur("terms_ar")}
              error={getFieldError("terms_ar", indexData.terms_ar)}
              helperText={errors.terms_ar?.message || `${indexData.terms_ar?.length || 0}/512`}
              inputProps={{ maxLength: 512 }}
              multiline
              rows={5}
            />
          </div>
        </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitLoading}>{t('Index.cancel')}</Button>
        <Button 
          onClick={handleSubmit}
          disabled={submitLoading}
          startIcon={submitLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
        >
          {submitLoading ? 'Adding...' : t('Index.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddIndexDialog;